import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { pathFor, type Lang } from '../../../router/paths';

export default function Excursions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'de';
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const slides = [
    {
      image: '/images/ausflugsziele/nymphenburg.jpeg',
      label: t('exTeaserSlide1Label'),
      title: t('exTeaserSlide1Title'),
      description: t('exTeaserSlide1Desc'),
      duration: t('exTeaserSlide1Duration'),
    },
    {
      image: '/images/ausflugsziele/Ausflugsziele_Benediktenwang_Gipfelkreuz.jpg',
      label: t('exTeaserSlide2Label'),
      title: t('exTeaserSlide2Title'),
      description: t('exTeaserSlide2Desc'),
      duration: t('exTeaserSlide2Duration'),
      objectPosition: 'center calc(50% - 200px)',
    },
    {
      image: '/images/ausflugsziele/ammersee.jpeg',
      label: t('exTeaserSlide3Label'),
      title: t('exTeaserSlide3Title'),
      description: t('exTeaserSlide3Desc'),
      duration: t('exTeaserSlide3Duration'),
    },
    {
      image: '/images/ausflugsziele/starnbergersee_.jpeg',
      label: t('exTeaserSlide4Label'),
      title: t('exTeaserSlide4Title'),
      description: t('exTeaserSlide4Desc'),
      duration: t('exTeaserSlide4Duration'),
    },
  ];

  const goTo = useCallback((index: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 300);
  }, [animating]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, goTo, slides.length]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo, slides.length]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(c => (c + 1) % slides.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const slide = slides[current];

  return (
    <section id="ausfluege" className="py-12 sm:py-20 px-4 sm:px-6 bg-stone-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <p className="text-[10px] sm:text-xs font-semibold tracking-wider sm:tracking-widest uppercase text-amber-700 mb-4">{t('exTeaserKicker')}</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-gray-800 mb-4">
            {t('exTeaserTitle')}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            {t('exTeaserLead')}
          </p>
        </div>
      </div>

      <div className="relative w-full overflow-hidden" style={{ height: 'clamp(320px, 56vw, 560px)' }}>
        <img
          key={current}
          src={slide.image}
          alt={slide.title}
          className="w-full h-full object-cover transition-opacity duration-500"
          style={{ opacity: animating ? 0 : 1, objectPosition: (slide as any).objectPosition || 'center' }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />

        <div
          className="absolute inset-0 flex flex-col items-center justify-end sm:justify-center text-center px-4 sm:px-6 pb-10 sm:pb-0 transition-opacity duration-500"
          style={{ opacity: animating ? 0 : 1 }}
        >
          <p className="text-[10px] sm:text-xs font-semibold tracking-wider sm:tracking-widest uppercase text-amber-300 mb-2 sm:mb-3">
            {slide.label}
          </p>
          <h3 className="text-xl sm:text-4xl md:text-5xl font-serif text-white mb-2 sm:mb-5 drop-shadow-lg">
            {slide.title}
          </h3>
          <p className="text-white/90 text-xs sm:text-lg max-w-xl mb-4 sm:mb-8 drop-shadow px-2">
            {slide.description}
          </p>
          <button
            onClick={() => { navigate(pathFor('ausflugsziele', lang)); window.scrollTo({ top: 0, behavior: 'instant' }); }}
            className="inline-block border border-white text-white px-6 sm:px-8 py-3 rounded-md text-sm font-medium hover:bg-white hover:text-amber-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('exTeaserCta')}
          </button>
        </div>

        <button
          onClick={prev}
          aria-label={t('sliderPrev')}
          className="absolute left-2 sm:left-5 top-1/2 -translate-y-1/2 text-white/85 hover:text-white transition-colors cursor-pointer z-10 sm:flex sm:items-center sm:justify-center sm:w-11 sm:h-11 sm:rounded-full sm:bg-black/30 sm:hover:bg-black/55 [filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.7))] sm:[filter:none]"
        >
          <i className="ri-arrow-left-s-line text-4xl sm:text-2xl leading-none" />
        </button>

        <button
          onClick={next}
          aria-label={t('sliderNext')}
          className="absolute right-2 sm:right-5 top-1/2 -translate-y-1/2 text-white/85 hover:text-white transition-colors cursor-pointer z-10 sm:flex sm:items-center sm:justify-center sm:w-11 sm:h-11 sm:rounded-full sm:bg-black/30 sm:hover:bg-black/55 [filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.7))] sm:[filter:none]"
        >
          <i className="ri-arrow-right-s-line text-4xl sm:text-2xl leading-none" />
        </button>

        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`slide ${i + 1}`}
              className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-all cursor-pointer ${
                i === current ? 'bg-white scale-125' : 'bg-white/45 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
